import React, { ReactNode, useState } from "react";
import { useRouter } from "next/router";
import styled from "styled-components";
import {
  colors,
  font,
  media,
  Theme,
  Image,
} from "@literati/public-ui-shared-react";
import { makeQueryString } from "utils";
import {
  AFFILIATE_SEARCH_SCHOOL,
  AFFILIATE_SEARCH_TEACHER,
} from "utils/hooks/useAffiliateSearch";
import { SchoolBoxAffiliationSearch } from "components/SchoolBox/SchoolBoxAffiliationSearch";
import { Affiliate, AffiliateType } from "components/SchoolBox/schoolBoxTypes";

const HeroContainer = styled.section`
  padding: 5rem 1rem;
  background-color: ${colors.frenchBlueTint4};
  overflow-x: clip;
`;

const StyledLeftImage = styled(Image)`
  position: absolute;
  inset: -80px auto auto -180px;
  opacity: 25%;
  display: none;

  ${media.md} {
    display: block;
  }

  ${media.xl} {
    opacity: 100%;
    inset: -80px auto auto -150px;
  }
`;

const StyledRightImage = styled(Image)`
  position: absolute;
  inset: 32px -280px auto auto;
  opacity: 25%;
  display: none;

  ${media.md} {
    display: block;
  }

  ${media.xl} {
    opacity: 100%;
    inset: 32px -220px auto auto;
  }
`;

const HeroContent = styled.div`
  max-width: 550px;
  margin: 0 auto;
  text-align: center;
  position: relative;

  ${media.lg} {
    max-width: 740px;
  }
`;

const HeroContentInner = styled.div`
  position: relative;
  z-index: 1;
`;

const Eyebrow = styled.p`
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
  font-weight: ${font.weight.black};
  text-transform: uppercase;
  line-height: 120%;
  letter-spacing: 0.16688rem;
`;

const Title = styled.h1`
  margin-bottom: 1.5rem;
  font-family: ${font.fontFamily.hoeflerTitling};
  font-size: 2.25rem;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;

  ${media.lg} {
    font-size: 3rem;
  }
`;

const Text = styled.p`
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;

  ${media.lg} {
    font-size: 1.25rem;
  }
`;

export interface SchoolBoxAffiliationHeroProps {
  affiliate?: Affiliate | null;
  subtext?: ReactNode;
}

export const SchoolBoxAffiliationHero = ({
  affiliate,
  subtext,
}: SchoolBoxAffiliationHeroProps) => {
  const [type] = useState<AffiliateType>(
    affiliate
      ? "classroom" in affiliate
        ? AFFILIATE_SEARCH_TEACHER
        : AFFILIATE_SEARCH_SCHOOL
      : AFFILIATE_SEARCH_SCHOOL
  );

  const router = useRouter();

  // Send them to the next screen
  const handleSubmit = (affiliateId: string) => {
    const extAffiliateCode = affiliateId;
    router.push(`/subscribe${makeQueryString({ extAffiliateCode })}`);
  };

  return (
    <Theme>
      <HeroContainer>
        <StyledLeftImage
          contain
          src="https://siren-images.literati.com/images/schoolbox_bookstack.original.png"
          alt="decorative image of literati box and books"
          height={600}
          width={600}
        />
        <StyledRightImage
          contain
          src="https://siren-images.literati.com/images/schoolbox_bookbox.original.png"
          alt="decorative image of literati box and books"
          height={432}
          width={600}
        />
        <HeroContent>
          <HeroContentInner>
            <Eyebrow>Welcome to our Book Clubs</Eyebrow>
            <Title>
              Raise a reader and support their school at the same time.
            </Title>
            {subtext ? <Text>{subtext}</Text> : null}
            <SchoolBoxAffiliationSearch
              initialSelectedItem={affiliate}
              onSubmit={handleSubmit}
              type={type}
            />
          </HeroContentInner>
        </HeroContent>
      </HeroContainer>
    </Theme>
  );
};
